import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/sections";
import { useBlog, useBlogs } from "../hooks/useBlogs";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function BlogPost() {
  const { slug } = useParams();
  const { data: post, isLoading, isError } = useBlog(slug);
  const { data: allPosts = [] } = useBlogs();

  if (isLoading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#4ef72d]" size={32} /></div>;
  }
  if (isError || !post) return <Navigate to="/blog" replace />;

  const more = allPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div>
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <motion.img initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 1.4 }} src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A221C] via-[#0A221C]/50 to-[#0A221C]/20" />
        <div className="ll-container relative pb-14">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-[#4ef72d] text-sm mb-5"><ArrowLeft size={16} /> All Stories</Link>
          <span className="inline-block badge-dark mb-4">{post.category}</span>
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight max-w-4xl">{post.title}</h1>
          <p className="text-white/80 text-sm mt-4 flex items-center gap-2"><Clock size={15} /> {fmtDate(post.date)} · {post.read_time} · By {post.author}</p>
        </div>
      </section>

      <article className="ll-container py-16 max-w-3xl" data-testid="blog-article">
        <Reveal>
          <p className="font-display text-2xl md:text-3xl leading-snug text-[#0A221C] mb-10">{post.excerpt}</p>
          <div className="space-y-6">
            {(post.content || []).map((para, i) => (
              <p key={i} className="text-[#4A5D58] text-lg leading-relaxed">{para}</p>
            ))}
          </div>

          <div className="mt-12 p-8 rounded-3xl bg-[#0A221C] text-white">
            <h3 className="font-display text-2xl font-bold">Ready to plan this trip?</h3>
            <p className="text-white/70 mt-2">Our travel experts craft the itinerary around you. Get an honest quote today.</p>
            <Link to="/contact" className="btn-primary mt-6">Enquire Now <ArrowRight size={18} /></Link>
          </div>
        </Reveal>
      </article>

      {more.length > 0 && (
        <section className="ll-container pb-20">
          <h2 className="font-display text-3xl font-bold mb-8">More stories</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {more.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="ll-card group block">
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out" />
                </div>
                <div className="p-6">
                  <p className="text-xs text-[#4A5D58]">{fmtDate(p.date)} · {p.read_time}</p>
                  <h3 className="font-display text-xl font-bold mt-2 group-hover:text-[#4ef72d] transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="pb-24"><CTABand /></div>
    </div>
  );
}
